// The online IDE's Modules tab (docs/MODULE_MANAGER_DESIGN.md): the Modules
// page (web/ide/modules), in a frame in a tab beside Help, and the host the
// page asks for the few things outside it, from the open project. Like the
// desktop IDEs' hosts, this knows nothing about modules: it lists, reads and
// writes files in the program's modules folder, after checking each path,
// edits the main file through the editor, so that Undo takes the Import line
// back, and reads the open file and the folder beside it, to upload a module
// (web/ide/modules/src/host.js has the protocol).
//
// Installed modules are ordinary project files: they sync to the account,
// go into share links, zips and web builds, and the compiler finds them in
// the program's own modules folder.

import { pathProblem } from "../../server/lib/module_rules.mjs";
import { base64 } from "./cloud.js";

const PROTOCOL = 1;
const OPEN = "b3d-ide:modules-open";
const WRITE_LIMIT = 64 << 20;
// The page's views the IDE opens it on.
const VIEWS = { find: "find?focus", upload: "upload", account: "account" };

async function sha256(bytes) {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

// ide: { $, cloud, remember, editor, tabs, closeMenu, status, project(), openFile(path), showTab(), hideTab(),
// refreshFiles(), fileChanged(path), openAccount() }
export function setupModules(ide) {
  const { $, cloud } = ide;
  const items = ["menu-modules", "menu-add-module", "menu-upload-module"];
  if (!cloud || !cloud.config || !cloud.config.modules) {
    for (const id of items) if ($(id)) $(id).hidden = true;
    return null;
  }
  for (const id of items) if ($(id)) $(id).hidden = false;
  // The account dialog's computers signed in to the desktop Modules tabs, and
  // deleting the account, on the page's Account view.
  if ($("account-more")) {
    $("account-more").hidden = false;
    for (const id of ["account-computers", "account-delete"]) {
      $(id).addEventListener("click", () => {
        $("account-dialog").close();
        show("account");
      });
    }
  }
  const pane = $("modules-pane");
  let frame = null;

  const send = (message) => {
    if (frame && frame.contentWindow) frame.contentWindow.postMessage({ b3dModules: 1, ...message }, location.origin);
  };
  const event = (name, data = {}) => send({ event: name, data });

  // The program's folder: the main file's, which the compiler searches.
  const programFolder = () => {
    const project = ide.project();
    const main = project && project.main;
    return main && main.includes("/") ? main.slice(0, main.lastIndexOf("/") + 1) : "";
  };
  const modulesFolder = () => `${programFolder()}modules/`;
  const checked = (path) => {
    const problem = pathProblem(path);
    if (problem) throw new Error(problem);
    return path;
  };
  // The open file the upload reads from, and its folder.
  let upload = null;

  const operations = {
    hello: () => {
      const project = ide.project();
      return {
        host: "online", platform: "web", product: typeof B3D_SITE_VERSION === "string" ? B3D_SITE_VERSION : "",
        flavour: "extended", protocol: PROTOCOL, computer: null,
        capabilities: ["object-lifecycle-v1"],
        program: project && project.main ? { name: project.name, main: project.main, mode: "extended" } : null,
        reason: project && !project.main ? "unsaved" : null,
      };
    },
    listFiles: async () => {
      const project = ide.project();
      const root = modulesFolder();
      const files = [];
      if (!project) return { files };
      for (const path of project.paths()) {
        if (!path.startsWith(root)) continue;
        const bytes = await project.bytes(path);
        files.push({ path: path.slice(root.length), size: bytes.length, sha256: await sha256(bytes) });
      }
      return { files };
    },
    readFiles: async ({ paths, from }) => {
      const project = ide.project();
      if (!Array.isArray(paths)) throw new Error("Name the files");
      const files = [];
      for (const path of paths) {
        let full;
        if (from === "upload") {
          if (!upload) throw new Error("Choose Upload this module first");
          const relative = checked(path);
          if (relative !== upload.mainName && !relative.startsWith(`${upload.folderName}/`)) throw new Error(`${path} isn't part of the upload`);
          full = upload.base + relative;
        } else {
          full = modulesFolder() + checked(path);
        }
        if (!project.has(full)) throw new Error(`There is no ${full}`);
        const text = ide.editor.textOf(full);
        const bytes = text !== undefined ? new TextEncoder().encode(text) : await project.bytes(full);
        files.push({ path, bytes: base64.encode(bytes) });
      }
      return { files };
    },
    writeFiles: async ({ write = [], remove = [] }) => {
      const project = ide.project();
      if (!project || !project.main) throw new Error("Save your program first");
      const root = modulesFolder();
      let total = 0;
      const decoded = write.map((item) => {
        const bytes = base64.decode(item.bytes);
        total += bytes.length;
        return { path: root + checked(item.path), bytes };
      });
      for (const path of remove) checked(path);
      if (total > WRITE_LIMIT) throw new Error("That change is too large");
      // Every file is decoded and checked before the first is written.
      for (const item of decoded) {
        await project.write(item.path, item.bytes);
        ide.fileChanged(item.path);
      }
      for (const path of remove) {
        if (project.has(root + path)) {
          await project.remove(root + path);
          ide.fileChanged(root + path, true);
        }
      }
      ide.refreshFiles();
      return {};
    },
    readMain: async () => {
      const project = ide.project();
      if (!project || !project.main) return null;
      const text = ide.editor.textOf(project.main);
      return { path: project.main, text: text !== undefined ? text : await project.text(project.main) };
    },
    editMain: async ({ line, remove, insert }) => {
      const project = ide.project();
      if (!project || !project.main) throw new Error("The program has no main file");
      const path = project.main;
      const text = ide.editor.textOf(path) ?? await project.text(path);
      const lines = text.split("\n");
      if (!Number.isInteger(line) || line < 0 || line > lines.length) throw new Error("No such line");
      let from = 0;
      for (let k = 0; k < line; ++k) from += lines[k].length + 1;
      let to = from;
      if (remove !== null && remove !== undefined) {
        if ((lines[line] || "").replace(/\r$/, "") !== String(remove).replace(/\r$/, "")) {
          throw new Error(`${path} changed: line ${line + 1} no longer says ${remove}`);
        }
        to = from + lines[line].length + (line < lines.length - 1 ? 1 : 0);
      }
      const inserted = insert !== null && insert !== undefined ? `${insert}\n` : "";
      // Inserting after the last line, which has no newline of its own.
      let start = from;
      let added = inserted;
      if (from > text.length) {
        start = text.length;
        added = `\n${insert}`;
      }
      ide.editor.edit(path, text, { from: start, to, insert: added });
      ide.status(insert ? `Added ${insert} to ${path}: Ctrl+Z takes it back.` : `Removed ${remove} from ${path}.`);
      return {};
    },
    openFile: async ({ path }) => {
      const full = modulesFolder() + checked(path);
      if (!ide.project() || !ide.project().has(full)) throw new Error(`There is no ${full}`);
      await ide.openFile(full);
      return {};
    },
    uploadFolder: async () => {
      const project = ide.project();
      const open = ide.editor.path;
      if (!project || !open || !project.has(open)) throw new Error("Open the module's main file first");
      const base = open.includes("/") ? open.slice(0, open.lastIndexOf("/") + 1) : "";
      const mainName = open.slice(base.length);
      const folderName = mainName.replace(/\.[^.]*$/, "");
      const prefix = `${base}${folderName}/`;
      upload = { base, mainName, folderName };
      const text = ide.editor.textOf(open) ?? await project.text(open);
      const mainBytes = new TextEncoder().encode(text);
      const files = [];
      for (const path of project.paths()) {
        if (!path.startsWith(prefix)) continue;
        const bytes = await project.bytes(path);
        files.push({ path: path.slice(prefix.length), size: bytes.length, sha256: await sha256(bytes) });
      }
      return { main: { name: mainName, text, size: mainBytes.length, sha256: await sha256(mainBytes) }, folder: folderName, files };
    },
    openBrowser: ({ path }) => {
      if (typeof path !== "string" || !path.startsWith("/")) throw new Error("Not a page of this site");
      window.open(new URL(path, location.origin).href, "_blank", "noopener");
      return {};
    },
    openAccount: () => {
      ide.openAccount();
      return {};
    },
    // The page signed in or out itself.
    accountChanged: async () => {
      await ide.accountChanged();
      return {};
    },
  };

  addEventListener("message", async (message) => {
    if (!frame || message.source !== frame.contentWindow || message.origin !== location.origin) return;
    const request = message.data;
    if (!request || request.b3dModules !== 1 || typeof request.op !== "string" || !Object.hasOwn(operations, request.op)) return;
    try {
      const result = await operations[request.op](request.args || {});
      send({ id: request.id, ok: true, result });
    } catch (error) {
      send({ id: request.id, ok: false, error: String(error && error.message || error) });
    }
  });

  // Shows the tab, making the frame the first time; view asks the page for
  // Find or Upload.
  const show = (view = null) => {
    ide.closeMenu();
    ide.remember.set(OPEN, "1");
    if (!frame) {
      frame = document.createElement("iframe");
      frame.id = "modules-frame";
      frame.title = "Modules";
      // Publishing asks for the member's passkey in the page.
      frame.allow = "publickey-credentials-get 'self'; publickey-credentials-create 'self'";
      frame.src = `modules/index.html${view ? `#${VIEWS[view] || VIEWS.find}` : ""}`;
      pane.append(frame);
    } else if (view) {
      event("show", { view });
    }
    ide.showTab();
  };

  $("menu-modules").addEventListener("click", () => show());
  $("menu-add-module").addEventListener("click", () => show("find"));
  $("menu-upload-module").addEventListener("click", () => show("upload"));

  return {
    show,
    // The tab was open when the IDE was last used.
    get wasOpen() { return ide.remember.get(OPEN) === "1"; },
    closed() { ide.remember.set(OPEN, "0"); },
    projectChanged() { event("program"); },
    filesChanged() { event("files"); },
    accountChanged() { event("account"); },
  };
}
